import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsync } from '../../core/useAsync';
import { ApiError, clearActiveLeague, getActiveLeagueId, getAccountMe, getLeagueRoster, getMyLeagues, joinLeague, leaveLeague, putAccountProfile, setActiveLeagueId, type Account, type MyLeague } from '../../core/api';
import { signOut } from '../../core/msal';
import { useTheme } from '../useTheme';
import { ThemeSwitch } from '../ThemeSwitch';
import { CreateLeagueWizard } from './CreateLeagueWizard';
import { fileToAvatarDataUrl } from '../avatar';
import { avatarColor, avatarStyle, gradFor, initials } from '../format';
import type { RosterMember } from '../../core/types';
import s from './LeaguesHub.module.css';

// The signed-in home: the leagues you're in, plus create + join, and your global
// profile (name + photo). Entering a league stores it as the active league
// (X-League-Id on every call) and drops into /app.

const PHASE: Record<string, string> = { SCHEDULED: 'scheduled', SUBMITTING: 'submissions open', VOTING: 'voting open', CLOSED: 'between rounds' };
function statusOf(st: MyLeague['status']): { text: string; live: boolean } {
  if (st.paused) return { text: 'Paused', live: false };
  const phase = st.phase ? (PHASE[st.phase] ?? st.phase.toLowerCase()) : 'no round yet';
  const live = st.phase === 'VOTING' || st.phase === 'SUBMITTING';
  return { text: st.round_no ? `Round ${st.round_no} · ${phase}` : phase, live };
}

// Avatar photo if set, else the coloured initials.
function avStyle(url: string | null, seed: string) {
  return url ? { backgroundImage: `url(${url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : { background: avatarColor(seed) };
}

const Plus = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>;
const Key = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="15" r="4" /><path d="m10.5 12.5 6-6M15 7l2 2M18 4l2 2" /></svg>;
const Shield = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
const Gear = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>;

export function LeaguesHub({ isPlatformOwner = false }: { isPlatformOwner?: boolean }) {
  const { mode } = useTheme();
  const navigate = useNavigate();
  const leagues = useAsync<MyLeague[]>(getMyLeagues, []);
  const account = useAsync<{ account: Account; leagues: unknown[] }>(getAccountMe, []);
  const fileRef = useRef<HTMLInputElement>(null);
  const [panel, setPanel] = useState<'none' | 'join' | 'profile'>('none');
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState('');
  const [pname, setPname] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const displayName = account.data?.account.display_name ?? 'you';
  const avatarUrl = account.data?.account.avatar_url ?? null;
  const enter = (id: number) => { setActiveLeagueId(id); navigate('/app'); };
  // Owners jump straight to the ONE admin hub (the Admin console) from a league card —
  // same destination as the in-league "Manage" button, so there's a single place to
  // run a league (rounds, proposals, and a link into League settings from there).
  const openManage = (id: number) => { setActiveLeagueId(id); navigate('/app/admin'); };

  const [leaving, setLeaving] = useState<number | null>(null);

  // Per-league member rosters, for the avatar stack on each card. Fetched lazily after
  // the leagues load (best-effort; a card just shows no stack if it fails).
  const [rosters, setRosters] = useState<Record<number, RosterMember[]>>({});
  useEffect(() => {
    for (const l of leagues.data ?? []) {
      if (rosters[l.league_id]) continue;
      getLeagueRoster(l.league_id).then((r) => setRosters((prev) => ({ ...prev, [l.league_id]: r })));
    }
  }, [leagues.data]); // eslint-disable-line react-hooks/exhaustive-deps
  async function leave(id: number, name: string) {
    if (leaving != null || !window.confirm(`Leave “${name}”? Your history is kept, but you’ll need the invite code to rejoin.`)) return;
    setLeaving(id); setErr(null);
    try {
      await leaveLeague(id);
      if (getActiveLeagueId() === id) clearActiveLeague();
      leagues.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not leave the league.');
    } finally { setLeaving(null); }
  }

  async function doSaveName() {
    const n = pname.trim();
    if (!n || busy) return;
    setBusy(true); setErr(null);
    try {
      await putAccountProfile({ display_name: n });
      account.reload();
      setPanel('none');
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not save your name — try again.'); } finally { setBusy(false); }
  }

  async function onPickAvatar(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || busy) return;
    setBusy(true); setErr(null);
    try {
      const avatar_url = await fileToAvatarDataUrl(file);
      await putAccountProfile({ avatar_url });
      account.reload();
    } catch { setErr('Could not update your photo — try a different image.'); } finally { setBusy(false); }
  }

  async function removeAvatar() {
    if (busy) return;
    setBusy(true); setErr(null);
    try { await putAccountProfile({ avatar_url: null }); account.reload(); } catch { setErr('Could not remove your photo.'); } finally { setBusy(false); }
  }

  async function doJoin() {
    const c = code.trim();
    if (!c || busy) return;
    setBusy(true); setErr(null);
    try {
      const l = await joinLeague(c);
      leagues.reload();
      enter(l.league_id);
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'That code didn’t work — check it and try again.'); } finally { setBusy(false); }
  }

  const list = leagues.data ?? [];

  return (
    <div className="tp-root" data-mode={mode}>
      <div className={s.wrap}>
        <header className={s.top}>
          <span className={s.brand}><span className={s.mk}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M9 18V6l10-2v12" /><circle cx="6" cy="18" r="3" /><circle cx="16" cy="16" r="3" /></svg></span>TPIAP</span>
          <div className={s.topr}>
            {isPlatformOwner ? (
              <button className={s.owner} onClick={() => navigate('/owner')} title="Platform owner console">
                <Shield />Owner
              </button>
            ) : null}
            <ThemeSwitch />
            <button className={s.me} onClick={() => { setPname(displayName === 'you' ? '' : displayName); setPanel('profile'); setErr(null); }} title="Your profile">
              <span className={s.av} style={avStyle(avatarUrl, displayName)}>{avatarUrl ? '' : initials(displayName)}</span><b>{displayName}</b>
            </button>
          </div>
        </header>

        {creating ? (
          <CreateLeagueWizard onCancel={() => { setCreating(false); leagues.reload(); }} onCreated={() => navigate('/app')} />
        ) : (
          <div className={s.body}>
            <div className={s.head}>
              <div>
                <span className={s.eyebrow}>Your account</span>
                <h1>Your leagues</h1>
                <p>Pick one to jump in, or start your own. <button className={s.whatlink} onClick={() => navigate('/about')}>What is this?</button></p>
              </div>
              <div className={s.actions}>
                <button className={s.btn} onClick={() => { setPanel(panel === 'join' ? 'none' : 'join'); setErr(null); }}><Key />Join with a code</button>
                <button className={`${s.btn} ${s.primary}`} onClick={() => { setCreating(true); setErr(null); }}><Plus />Create a league</button>
              </div>
            </div>

            {panel === 'join' ? (
              <div className={s.panel}>
                <span className={s.eyebrow}>Join a league</span>
                <div className={s.field}>
                  <input placeholder="Enter a join code…" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') doJoin(); }} />
                  <button className={`${s.btn} ${s.primary}`} onClick={doJoin} disabled={busy || !code.trim()}>{busy ? '…' : 'Join'}</button>
                </div>
                <p className={s.hint}>Ask a league owner for their code — leagues are invite-only.</p>
              </div>
            ) : null}
            {panel === 'profile' ? (
              <div className={s.panel}>
                <span className={s.eyebrow}>Your profile</span>
                <div className={s.prow}>
                  <span className={s.pav} style={avStyle(avatarUrl, displayName)}>{avatarUrl ? '' : initials(displayName)}</span>
                  <div className={s.pact}>
                    <button className={s.btn} onClick={() => fileRef.current?.click()} disabled={busy}>{busy ? '…' : avatarUrl ? 'Change photo' : 'Add a photo'}</button>
                    {avatarUrl ? <button className={s.signout} onClick={removeAvatar} disabled={busy}>Remove</button> : null}
                    <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickAvatar} />
                  </div>
                </div>
                <div className={s.field}>
                  <input placeholder="What should we call you?" value={pname} onChange={(e) => setPname(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') doSaveName(); }} maxLength={60} />
                  <button className={`${s.btn} ${s.primary}`} onClick={doSaveName} disabled={busy || !pname.trim()}>{busy ? '…' : 'Save'}</button>
                </div>
                <p className={s.hint}>Your name and photo, across every league.</p>
                <div className={s.psignout}>
                  <button className={s.signout} onClick={() => signOut()}>Sign out</button>
                </div>
              </div>
            ) : null}
            {err ? <p className={s.hint} style={{ color: 'var(--video)' }}>{err}</p> : null}

            {leagues.loading ? (
              <div className={s.state}>Loading your leagues…</div>
            ) : list.length === 0 ? (
              <div className={s.empty}>
                <b>No leagues yet</b>
                You’re not in any leagues. Create one and invite your mates, or join with a code.
              </div>
            ) : (
              <div className={s.grid}>
                {list.map((l) => {
                  const st = statusOf(l.status);
                  return (
                    <div key={l.league_id} className={s.league}>
                      <button className={s.leaguemain} onClick={() => enter(l.league_id)}>
                        <span className={s.cov} style={{ background: gradFor(l.league_id) }}>
                          <span className={`${s.role}${l.role === 'owner' ? ' ' + s.owner : ''}`}>{l.role === 'owner' ? '★ Owner' : 'Member'}</span>
                          {(rosters[l.league_id]?.length ?? 0) > 0 ? (
                            <span className={s.stack}>
                              {rosters[l.league_id].slice(0, 5).map((m) => (
                                <span key={m.user_id} className={s.chip} style={avatarStyle(m.avatar_url, m.user_id)} title={m.display_name}>{m.avatar_url ? '' : initials(m.display_name)}</span>
                              ))}
                              {rosters[l.league_id].length > 5 ? <span className={`${s.chip} ${s.more}`}>+{rosters[l.league_id].length - 5}</span> : null}
                            </span>
                          ) : null}
                        </span>
                        <span className={s.lb}>
                          <h3>{l.name}</h3>
                          <span className={s.meta}>{l.player_count} player{l.player_count === 1 ? '' : 's'}</span>
                          {l.player_count < 2 ? (
                            <span className={s.nudge}>{l.role === 'owner' ? 'Invite a friend to start — a round needs 2+ players' : 'Waiting on more players to start'}</span>
                          ) : (
                            <span className={`${s.stat}${st.live ? '' : ' ' + s.paused}`}><span className={s.live} />{st.text}</span>
                          )}
                          <span className={s.enter}><span className={s.go}>Enter →</span></span>
                        </span>
                      </button>
                      {l.role === 'owner' ? (
                        <button className={s.gear} onClick={() => openManage(l.league_id)} title="Manage league" aria-label={`Manage ${l.name}`}>
                          <Gear />Manage
                        </button>
                      ) : (
                        <button className={s.leave} disabled={leaving === l.league_id} onClick={() => leave(l.league_id, l.name)} title="Leave league" aria-label={`Leave ${l.name}`}>
                          {leaving === l.league_id ? '…' : 'Leave'}
                        </button>
                      )}
                    </div>
                  );
                })}
                <button className={s.add} onClick={() => { setCreating(true); setErr(null); }}>
                  <span className={s.addic}><Plus /></span>
                  <span><b>Start a league</b><br /><small>You’re the owner · get a code to invite your mates</small></span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
